import { Card, Divider, Stack, Switch, Text } from '@mantine/core';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import CompanyLogoCard from 'src/secret-menu/auto-brand/steps/CompanyLogoCard';
import SlotCard from 'src/secret-menu/auto-brand/steps/SlotCard';
import type { AutoBrandData, AutoBrandLogoSlot } from 'src/secret-menu/auto-brand/types';
import { DEFAULT_CONFIG, type HeaderAlignment } from 'src/secret-menu/export-assets/types';

const FIRST_PAGE_HEADER = {
  slot: 'first_page',
  label: 'First page header',
  description: 'Logo shown in the header of the first page only.',
};

const SUBSEQUENT_HEADER = {
  slot: 'subsequent_page',
  label: 'Subsequent page header',
  description: 'Logo shown in the header of every page after the first.',
};

const FOOTER_SLOT = {
  slot: 'footer',
  label: 'Footer',
  description: 'Logo shown in the footer of every page.',
};

export default function UploadLogosStep(props: StepProps<AutoBrandData>) {
  const { data, setData, nextStep, previousStep, canGoBack } = props;
  const config = data.config ?? data.existingBranding?.config ?? DEFAULT_CONFIG;
  const companyLogo = data.logoUploads.companyLogo;
  const companyLogoAvailable = companyLogo !== null;

  const setCompanyLogo = (file: File | null) => {
    setData(prev => {
      if (file !== null) {
        return {
          ...prev,
          logoUploads: { ...prev.logoUploads, companyLogo: file },
        };
      }
      const slots = { ...prev.logoUploads.slots };
      (Object.keys(slots) as AutoBrandLogoSlot[]).forEach(slot => {
        if (slots[slot].useCompanyLogo) {
          slots[slot] = { ...slots[slot], useCompanyLogo: false };
        }
      });
      return {
        ...prev,
        logoUploads: { companyLogo: null, slots },
      };
    });
  };

  const setSlotFile = (slot: AutoBrandLogoSlot, file: File | null) => {
    setData(prev => ({
      ...prev,
      logoUploads: {
        ...prev.logoUploads,
        slots: {
          ...prev.logoUploads.slots,
          [slot]: { ...prev.logoUploads.slots[slot], file },
        },
      },
    }));
  };

  const setSlotUseCompanyLogo = (slot: AutoBrandLogoSlot, value: boolean) => {
    setData(prev => ({
      ...prev,
      logoUploads: {
        ...prev.logoUploads,
        slots: {
          ...prev.logoUploads.slots,
          [slot]: {
            ...prev.logoUploads.slots[slot],
            useCompanyLogo: value,
            file: value ? null : prev.logoUploads.slots[slot].file,
          },
        },
      },
    }));
  };

  const setAlignment = (
    slot: 'first_page_header' | 'subsequent_page_header' | 'footer',
    alignment: HeaderAlignment,
  ) => {
    setData(prev => {
      const prevConfig = prev.config ?? config;
      if (slot === 'footer') {
        return {
          ...prev,
          config: { ...prevConfig, footer: { ...prevConfig.footer, logo_alignment: alignment } },
        };
      }
      return {
        ...prev,
        config: { ...prevConfig, [slot]: { ...prevConfig[slot], alignment } },
      };
    });
  };

  const setDifferentFirstPage = (value: boolean) => {
    setData(prev => ({
      ...prev,
      config: {
        ...(prev.config ?? config),
        different_first_page_header: value,
      },
    }));
  };

  const setShowPageNumbers = (value: boolean) => {
    setData(prev => {
      const prevConfig = prev.config ?? config;
      return {
        ...prev,
        config: {
          ...prevConfig,
          footer: { ...prevConfig.footer, show_page_numbers: value },
        },
      };
    });
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Logos and page layout"
      description="Upload the company logo and configure the headers and footers for the tenant's exports. We resize logos on the server so they always fit the header."
      continueText="Continue"
      showClientName={false}
      hideProgress
      allowVerticalOverflow
      canGoBack={canGoBack}
      previousStep={() => previousStep()}
      nextStep={() => nextStep()}
    >
      <Stack gap="lg">
        <CompanyLogoCard file={companyLogo} onChange={setCompanyLogo} />

        <Card withBorder radius="md" p="md">
          <Stack gap="sm">
            <Text fw={FONT_WEIGHTS.semibold}>Page layout</Text>
            <Switch
              label="Different first page header"
              description="Render a separate header on the first page only."
              checked={config.different_first_page_header}
              onChange={e => setDifferentFirstPage(e.currentTarget.checked)}
            />
            <Switch
              label="Show page numbers"
              description="Displays 'Page X of Y' on the opposite side from the footer logo."
              checked={config.footer.show_page_numbers}
              onChange={e => setShowPageNumbers(e.currentTarget.checked)}
            />
          </Stack>
        </Card>

        <Divider label="Header & footer slots" labelPosition="center" />

        {config.different_first_page_header && (
          <SlotCard
            definition={FIRST_PAGE_HEADER}
            file={data.logoUploads.slots.first_page.file}
            useCompanyLogo={data.logoUploads.slots.first_page.useCompanyLogo}
            companyLogoAvailable={companyLogoAvailable}
            alignment={config.first_page_header.alignment}
            onFileChange={file => setSlotFile('first_page', file)}
            onUseCompanyLogoChange={value => setSlotUseCompanyLogo('first_page', value)}
            onAlignmentChange={value => setAlignment('first_page_header', value)}
          />
        )}

        <SlotCard
          definition={
            config.different_first_page_header
              ? SUBSEQUENT_HEADER
              : { ...SUBSEQUENT_HEADER, label: 'Page header' }
          }
          file={data.logoUploads.slots.subsequent_page.file}
          useCompanyLogo={data.logoUploads.slots.subsequent_page.useCompanyLogo}
          companyLogoAvailable={companyLogoAvailable}
          alignment={config.subsequent_page_header.alignment}
          onFileChange={file => setSlotFile('subsequent_page', file)}
          onUseCompanyLogoChange={value => setSlotUseCompanyLogo('subsequent_page', value)}
          onAlignmentChange={value => setAlignment('subsequent_page_header', value)}
        />

        <SlotCard
          definition={FOOTER_SLOT}
          file={data.logoUploads.slots.footer.file}
          useCompanyLogo={data.logoUploads.slots.footer.useCompanyLogo}
          companyLogoAvailable={companyLogoAvailable}
          alignment={config.footer.logo_alignment}
          onFileChange={file => setSlotFile('footer', file)}
          onUseCompanyLogoChange={value => setSlotUseCompanyLogo('footer', value)}
          onAlignmentChange={value => setAlignment('footer', value)}
        />
      </Stack>
    </DrawerFlowScreenLayout>
  );
}
